/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, {
  useEffect,
  useState,
  forwardRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import * as S from './style';
import { useGeneralContext } from '../../context/GeneralContext';
import Notification from './Notification';
import { withClickOutside } from '../Wrappers/WithClickOutside';
import { putCRUD } from '../../services/crudService';
import Spinner from '../shared/atoms/Spinner';
import { useNotificationsContext } from '../../context/NotificationsContext';
import ActionButton from '../shared/atoms/ActionButton';

const NotificationContainer = forwardRef(
  ({ containerState, background }, ref) => {
    const {
      refetch,
      notifications,
      isFetching,
      subscribe,
      unsubscribe,
      connection,
    } = useNotificationsContext();
    const isSmallMobile = useMediaQuery('(max-width: 450px)');
    const [dismissing, setDismissing] = useState([]);
    const { openToast } = useGeneralContext();
    const { t } = useTranslation();

    useEffect(() => {
      if (containerState) refetch();
    }, [containerState, refetch]);

    const isConnecting = useMemo(
      () => connection.status === 'Connecting',
      [connection.status],
    );

    const isError = useMemo(
      () =>
        connection.status === 'Error' ||
        connection.status === 'ConnectionDisrupted',
      [connection.status],
    );

    const dismissAll = useCallback(async () => {
      try {
        setDismissing(notifications.map(notif => notif.id));
        await putCRUD({ entity: 'notification', id: 'all' });
        await refetch();
        openToast(
          t('components.notification_container.validation.delete_all_success'),
        );
      } catch (err) {
        console.error(err);
        openToast(
          t('components.notification_container.validation.delete_error'),
          'error',
        );
      } finally {
        setDismissing([]);
      }
    }, [notifications, openToast, refetch, t]);

    const singleDismiss = useCallback(
      async id => {
        try {
          setDismissing(prev => Array.from(new Set([...prev, id])));
          await putCRUD({ entity: 'notification', id });
          await refetch();
          openToast(
            t('components.notification_container.validation.delete_success'),
          );
        } catch (err) {
          console.error(err);
          openToast(
            t('components.notification_container.validation.delete_error'),
            'error',
          );
        } finally {
          setDismissing([]);
        }
      },
      [openToast, refetch, t],
    );

    return (
      <S.NotificationContainer
        $open={containerState}
        $isSmallMobile={isSmallMobile}
        ref={ref}
        style={{ backgroundColor: background || 'transparent' }}
      >
        <ActionButton
          onClick={connection.status === 'Connected' ? unsubscribe : subscribe}
          label={
            connection.status === 'Connected'
              ? t('components.notification_container.disconnect_button')
              : t('components.notification_container.connect_button')
          }
          minWidth="100%"
          margin="0 0 0.5rem 0"
          loading={isConnecting}
          disabled={isError}
        />
        {notifications?.length > 0 ? (
          <>
            <ActionButton
              warning
              onClick={dismissAll}
              label={t('components.notification_container.dismiss_button')}
              minWidth="100%"
              margin="0 0 0.5rem 0"
              loading={dismissing.length === notifications.length}
            />
            <S.NotificationList>
              <div>
                {notifications.map(notification => (
                  <Notification
                    key={notification.id}
                    notification={notification}
                    onDismiss={e => {
                      e.stopPropagation();
                      singleDismiss(notification.id);
                    }}
                    dismissing={dismissing.includes(notification.id)}
                  />
                ))}
              </div>
            </S.NotificationList>
          </>
        ) : (
          <S.NoNotifications>
            {t('components.notification_container.validation.empty')}
          </S.NoNotifications>
        )}
        {isFetching && <Spinner />}
      </S.NotificationContainer>
    );
  },
);

NotificationContainer.propTypes = {
  containerState: PropTypes.bool.isRequired,
  background: PropTypes.string,
};

NotificationContainer.displayName = 'NotificationContainer';

export default withClickOutside(NotificationContainer);
